import { useState, useEffect, useCallback } from 'react';
import { ValuationData, ValidationErrors, SavedValuation } from '../types/valuation';

const STORAGE_KEY = 'fuelCentral_valuations';

export function useValuationForm(initialData: ValuationData) {
  const [data, setData] = useState<ValuationData>(initialData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [savedValuations, setSavedValuations] = useState<SavedValuation[]>([]);

  // Load saved valuations from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setSavedValuations(JSON.parse(saved));
    }
  }, []);

  // Auto-save when data changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveProgress(data);
    }, 500); // Debounce auto-save by 500ms

    return () => clearTimeout(timeoutId);
  }, [data]);

  // Validate a single field
  const validateField = (field: keyof ValuationData, value: any): string => {
    // All fields are optional, so no validation errors
    return '';
  };

  const validateFinancialMetrics = (data: ValuationData) => {
    const errors: ValidationErrors = {};

    // Only validate if values are provided and are negative
    if (data.annualGallons && data.annualGallons < 0) {
      errors.annualGallons = 'Annual gallons cannot be negative';
    }

    if (data.fuelMargin && data.fuelMargin < 0) {
      errors.fuelMargin = 'Fuel margin cannot be negative';
    }

    if (data.carWashRevenue !== null && data.carWashRevenue !== undefined && data.carWashRevenue < 0) {
      errors.carWashRevenue = 'Car wash revenue cannot be negative';
    }

    if (data.foodServiceRevenue !== null && data.foodServiceRevenue !== undefined && data.foodServiceRevenue < 0) {
      errors.foodServiceRevenue = 'Food service revenue cannot be negative';
    }

    if (data.storeRevenue && data.storeRevenue < 0) {
      errors.storeRevenue = 'Store revenue cannot be negative';
    }

    if (data.operatingExpenses && data.operatingExpenses < 0) {
      errors.operatingExpenses = 'Operating expenses cannot be negative';
    }

    if (data.salaries && data.salaries < 0) {
      errors.salaries = 'Salaries cannot be negative';
    }

    if (data.utilities && data.utilities < 0) {
      errors.utilities = 'Utilities cannot be negative';
    }

    if (data.propertyTax && data.propertyTax < 0) {
      errors.propertyTax = 'Property tax cannot be negative';
    }

    return errors;
  };

  // Validate all fields
  const validateForm = async (): Promise<boolean> => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    Object.keys(data).forEach((key) => {
      const error = validateField(key as keyof ValuationData, data[key as keyof ValuationData]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    const financialErrors = validateFinancialMetrics(data);
    Object.assign(newErrors, financialErrors);
    if (Object.keys(financialErrors).length > 0) {
      isValid = false;
    }

    setErrors(newErrors);

    // Log errors to console for debugging
    if (!isValid) {
      console.error('Validation errors:', newErrors);
    }

    return isValid;
  };

  // Update a field and validate it
  const updateField = useCallback((updates: Partial<ValuationData>) => {
    console.log('=== useValuationForm: updateField called ===');
    console.log('Updates:', updates);
    if ('stationObjectId' in updates) {
      console.log('🔑 stationObjectId in updates:', updates.stationObjectId);
    }

    setData(prevData => {
      const newData = { ...prevData, ...updates };
      console.log('New data after merge:', {
        stationObjectId: newData.stationObjectId,
        location: newData.location
      });
      return newData;
    });

    // Validate updated fields
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      Object.keys(updates).forEach((key) => {
        const error = validateField(key as keyof ValuationData, updates[key as keyof ValuationData]);
        if (error) {
          newErrors[key] = error;
        } else {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  }, []);

  // Save current progress to localStorage
  const saveProgress = useCallback((formData: ValuationData = data) => {
    setSavedValuations(prevValuations => {
      const currentValuations = [...prevValuations];
      const timestamp = new Date().toISOString();
      
      // Update or create autosave entry
      const autosaveIndex = currentValuations.findIndex(v => v.id === 'autosave');
      const autosave: SavedValuation = {
        id: 'autosave',
        name: 'Autosave',
        data: formData,
        lastModified: timestamp
      };

      if (autosaveIndex >= 0) {
        currentValuations[autosaveIndex] = autosave;
      } else {
        currentValuations.push(autosave);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentValuations));
      return currentValuations;
    });
  }, [data]);

  // Save current valuation with a name
  const saveValuation = useCallback((name: string) => {
    setSavedValuations(prevValuations => {
      const currentValuations = [...prevValuations];
      const timestamp = new Date().toISOString();
      
      const newValuation: SavedValuation = {
        id: crypto.randomUUID(),
        name,
        data,
        lastModified: timestamp
      };

      currentValuations.push(newValuation);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentValuations));
      return currentValuations;
    });
  }, [data]);

  // Load a saved valuation
  const loadValuation = useCallback((id: string) => {
    const valuation = savedValuations.find(v => v.id === id);
    if (valuation) {
      setData(valuation.data);
      setErrors({});
    }
  }, [savedValuations]);

  return {
    data,
    errors,
    savedValuations,
    updateField,
    validateForm,
    validateField,
    saveValuation,
    loadValuation,
    saveProgress
  };
}
